import type { Container, Text } from "pixi.js";
import type { Spine } from "@esotericsoftware/spine-pixi-v8";
import { WinAmountLabel } from "./WinAmountLabel";
import type { SpriteText } from "../../sprite-text/SpriteText";
import { socketContainerToBone } from "../../spine/SpineSocket";
import { hideSlotAttachments } from "../../spine/hideSlotAttachments";
import { playClipAndWait } from "../../spine/SpineClipPlayer";
import type { WinAmountLabelSpineExplosionTuning } from "./WinAmountLabelSpineExplosionTuning";

/**
 * SPINE-EXPLOSION win-amount label variant: the digits are socketed to a Spine's
 * `number_placeholder` slot and ride the clips — `show()` plays `in` (the number pops in),
 * `popReplaceTo()` plays `explosion` (the number bursts to the multiplied total on the cannon fire).
 * `bumpAmount()` / `hide()` use the base defaults. Clips self-clock at the runner's `timeScale`.
 *
 * The Spine is game art, built by the game's label factory and handed in; the shell stays agnostic.
 * It must be ticked by the game's `SpineDriver` (autoUpdate off) so it pauses with the presentation.
 */
export class WinAmountLabelSpineExplosion extends WinAmountLabel {
  public readonly skeleton: Spine | null;
  private socketed = false;

  public constructor(input: { node?: Container; spriteText?: SpriteText | null; label?: Text | null; skeleton: Spine | null }) {
    super(input);
    this.skeleton = input.skeleton;
  }

  public override show(text: string): void {
    this.applyText(text);
    this.node.scale.set(1, 1);
    this.node.alpha = 1;
    this.ensureSocket();
    void this.playClip((this.tuning as WinAmountLabelSpineExplosionTuning | null)?.inClip);
  }

  public override async popReplaceTo(finalText: string): Promise<void> {
    this.applyText(finalText);
    const t = this.tuning as WinAmountLabelSpineExplosionTuning | null;
    await this.playClip(t?.explosionClip, t?.explosionSkipSeconds ?? 0);
  }

  /** Socket the digits (`spriteText`) to the placeholder bone ONCE + hide the Spine guide region. */
  private ensureSocket(): void {
    const t = this.tuning as WinAmountLabelSpineExplosionTuning | null;
    const skeleton = this.skeleton;
    if (this.socketed || !t || !skeleton || skeleton.destroyed || !this.spriteText) return;
    if (!socketContainerToBone(skeleton, t.placeholder, this.spriteText, t.digitScale)) return;
    hideSlotAttachments(skeleton, t.placeholder); // the digits render in the guide's place
    this.socketed = true;
  }

  /**
   * Play a one-shot Spine clip at the runner's timeScale; resolves when it ends. `skipSeconds` seeks
   * past the clip's start — used to skip the explosion's alpha fade-in so its burst is immediate.
   */
  private async playClip(name?: string, skipSeconds = 0): Promise<void> {
    if (!name || !this.skeleton || !this.taskRunner) return;
    await playClipAndWait(this.skeleton, this.taskRunner, name, { skipSeconds });
  }
}
